import { Card, EmptyState, Pagination, Skeleton } from "@heroui/react";
import { useEffect, useState } from "react";

import { useDebouncedValue } from "@/src/hooks/useDebouncedValue";
import { useTranslations } from "@/src/i18n/i18n";
import { PostCard } from "@/src/pages/Posts/components/PostCard";
import { PostsFilter } from "@/src/pages/Posts/components/PostsFilter";
import { useModerationPosts } from "@/src/pages/Posts/hooks/useModerationPosts";
import { usePostActions } from "@/src/pages/Posts/hooks/usePostActions";
import { usePostFilters } from "@/src/pages/Posts/hooks/usePostFilters";

/**
 * Le fil d'actualité, en entier (`/posts`).
 *
 * ## Une liste, dont la file de validation est un filtre
 *
 * L'écran ouvre sur **toutes** les publications — les quatre statuts, celles
 * que leur auteur a retirées comprises. La file à relever s'atteint par
 * `?status=pending_review`, et c'est par là que mène le tableau de bord.
 *
 * Le classifieur d'images **ne refuse jamais** : il décide de la visibilité et
 * met en attente ce dont il n'est pas sûr. Sans cet écran, une publication
 * retenue reste invisible **pour toujours** — rien d'autre ne peut la
 * débloquer. C'est aussi le seul endroit d'où les scores bruts sortent, et donc
 * la seule façon de calibrer le seuil sur des cas réels.
 *
 * ⚠️ **Les filtres vivent dans l'URL** (`usePostFilters`), pas dans un
 * `useState` : on ouvre une fiche depuis cette liste, et le retour arrière doit
 * ramener la même page des mêmes filtres.
 */
export function Posts() {
  const t = useTranslations("posts");
  const { filters, setFilters } = usePostFilters();

  // Le champ est piloté localement pour rester fluide sous la frappe ; l'URL
  // ne suit qu'après la pause, sinon chaque lettre écrirait une entrée
  // d'historique et une requête.
  const [term, setTerm] = useState(filters.q);
  const debounced = useDebouncedValue(term);

  useEffect(() => {
    if (debounced !== filters.q) setFilters({ q: debounced });
  }, [debounced, filters.q, setFilters]);

  const { posts, total, pageCount, isLoading, isError } =
    useModerationPosts(filters);
  const { approve, reject, destroy, pendingId } = usePostActions();

  /** Un filtre autre que le défaut est posé : le vide s'explique par lui. */
  const isFiltered =
    filters.q.trim() !== "" ||
    filters.status !== "all" ||
    filters.type !== "all" ||
    filters.deleted !== "all";

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <PostsFilter
        filters={filters}
        term={term}
        onChange={setFilters}
        onTermChange={setTerm}
      />

      {/*
        ⚠️ Le statut `scanning` mérite son propre mot : une publication qui y
        reste est une **anomalie**, pas une file d'attente. Le classement
        bascule seul en quelques secondes ; au-delà, la tâche de fond est morte
        sans écrire son verdict, et aucune reprise n'est planifiée.
      */}
      {filters.status === "scanning" && posts.length > 0 ? (
        <Card className="border-warning bg-warning-soft p-3">
          <Card.Content>
            <p className="text-sm text-pretty">{t("scanning-warning")}</p>
          </Card.Content>
        </Card>
      ) : null}

      {/* Le total ne s'affiche qu'une fois connu : « 0 publication » pendant le
          chargement dirait le contraire de la vérité. */}
      {isLoading || isError ? null : (
        <p className="text-sm text-muted">{t("total", { count: total })}</p>
      )}

      {isLoading ? (
        <div aria-hidden className="flex flex-col gap-3">
          {[0, 1, 2].map((row) => (
            <Skeleton className="h-56 w-full rounded-lg" key={row} />
          ))}
        </div>
      ) : null}

      {/* L'échec a son propre écran en plus du toast : sans cette branche, une
          panne se lit comme une liste vide — le message le plus rassurant
          possible, et le plus faux. */}
      {isError ? (
        <Notice description={t("error-description")} title={t("error-title")} />
      ) : null}

      {/*
        Une file vide se dit autrement qu'une liste vide : « rien n'attend de
        décision » est une bonne nouvelle, « aucune publication ne correspond »
        n'est qu'un constat.
      */}
      {!isLoading && !isError && posts.length === 0 ? (
        <Notice
          description={
            filters.status === "pending_review"
              ? t("empty-queue-description")
              : isFiltered
                ? t("empty-description")
                : t("empty-feed-description")
          }
          title={
            filters.status === "pending_review"
              ? t("empty-queue-title")
              : t("empty-title")
          }
        />
      ) : null}

      {!isLoading &&
        !isError &&
        posts.map((post) => (
          <PostCard
            isPending={pendingId === post.id}
            key={post.id}
            onApprove={approve}
            onDelete={destroy}
            onReject={reject}
            post={post}
          />
        ))}

      {/*
        Précédent / suivant plutôt qu'une rangée de numéros : le résumé dit où
        l'on est, ce que les numéros ne font qu'impliquer. Même parti que la
        liste des événements.
      */}
      {pageCount > 1 && !isLoading ? (
        <Pagination className="justify-between" size="sm">
          <Pagination.Previous
            isDisabled={filters.page <= 1}
            onPress={() => setFilters({ page: filters.page - 1 })}
          >
            <Pagination.PreviousIcon />
            {t("previous")}
          </Pagination.Previous>

          <Pagination.Summary>
            {t("page-of", { page: filters.page, pages: pageCount })}
          </Pagination.Summary>

          <Pagination.Next
            isDisabled={filters.page >= pageCount}
            onPress={() => setFilters({ page: filters.page + 1 })}
          >
            {t("next")}
            <Pagination.NextIcon />
          </Pagination.Next>
        </Pagination>
      ) : null}
    </div>
  );
}

function Notice({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card className="p-10">
      <Card.Content>
        <EmptyState className="flex flex-col items-center gap-2 text-center">
          <p className="text-base font-medium">{title}</p>
          <p className="max-w-md text-pretty text-muted">{description}</p>
        </EmptyState>
      </Card.Content>
    </Card>
  );
}
